export const STAFF_REGION_TOKEN_TIMEOUT_MS = 8_000;
export const REGIONAL_REQUEST_TIMEOUT_MS = 30_000;
export const CUSTOM_MEAL_ITEMS_STALE_MS = 30_000;

export type RegionalApiErrorKind = 'http' | 'network' | 'timeout' | 'unauthorized' | 'forbidden';

export class RegionalApiError extends Error {
  readonly kind: RegionalApiErrorKind;
  readonly status?: number;
  readonly reminted: boolean;
  /**
   * True only for a 401 minting the staff-region token itself (FOOD-1174): that request goes to
   * the Canada host with the Canada Bearer, so a 401 there means Canada rejected Canada, not the US API.
   */
  readonly mintFailure: boolean;

  constructor(opts: {
    kind: RegionalApiErrorKind;
    message: string;
    status?: number;
    reminted?: boolean;
    mintFailure?: boolean;
  }) {
    super(opts.message);
    this.name = 'RegionalApiError';
    this.kind = opts.kind;
    this.status = opts.status;
    this.reminted = opts.reminted ?? false;
    this.mintFailure = opts.mintFailure ?? false;
  }
}

export const formatRegionalError = (error: unknown): string => {
  if (error instanceof RegionalApiError) {
    if (error.kind === 'network') {
      return 'Network or CORS failure talking to the regional API.';
    }
    if (error.kind === 'timeout') {
      return 'The regional API timed out.';
    }
    if (error.kind === 'forbidden') {
      return 'Staff region token was denied (403).';
    }
    if (error.kind === 'unauthorized' && error.mintFailure) {
      return 'Your Canada session has expired. Sign in again.';
    }
    if (error.kind === 'unauthorized' && error.reminted) {
      return 'US API rejected the replacement staff token (401).';
    }
    if (error.kind === 'unauthorized') {
      return 'US API unauthorized (401).';
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

const abortSignalTimeout = (AbortSignal as typeof AbortSignal & { timeout?: (ms: number) => AbortSignal }).timeout;

/** Prefer `AbortSignal.timeout`; fall back when jsdom / older runtimes omit it. */
export const timeoutSignal = (ms: number): AbortSignal => {
  if (abortSignalTimeout) {
    return abortSignalTimeout.call(AbortSignal, ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
};

const abortSignalAny = (AbortSignal as typeof AbortSignal & { any?: (signals: AbortSignal[]) => AbortSignal }).any;

export const mergeAbortSignals = (signals: Array<AbortSignal | undefined>): AbortSignal => {
  const present = signals.filter((signal): signal is AbortSignal => !!signal);
  if (present.length === 1) {
    return present[0];
  }
  if (abortSignalAny) {
    return abortSignalAny.call(AbortSignal, present);
  }
  const controller = new AbortController();
  for (const signal of present) {
    if (signal.aborted) {
      controller.abort();
      return controller.signal;
    }
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller.signal;
};

export const classifyFetchError = (error: unknown): RegionalApiError => {
  if (error instanceof RegionalApiError) {
    return error;
  }
  const name = error instanceof DOMException || error instanceof Error ? error.name : '';
  if (name === 'TimeoutError' || (name === 'AbortError' && /timeout/i.test(String(error)))) {
    return new RegionalApiError({
      kind: 'timeout',
      message: 'Regional request timed out',
    });
  }
  if (name === 'AbortError') {
    throw error;
  }
  if (error instanceof TypeError) {
    return new RegionalApiError({
      kind: 'network',
      message: error.message || 'Regional request failed (network or CORS)',
    });
  }
  return new RegionalApiError({
    kind: 'http',
    message: error instanceof Error ? error.message : String(error),
  });
};

export const classifyHttpError = (
  response: Response,
  opts: { reminted?: boolean; mint?: boolean } = {},
): RegionalApiError => {
  if (response.status === 401) {
    return new RegionalApiError({
      kind: 'unauthorized',
      status: 401,
      reminted: opts.reminted,
      mintFailure: opts.mint,
      message: opts.mint
        ? 'Canada rejected the staff-region-token mint (401)'
        : opts.reminted
          ? 'US API rejected the replacement staff token (401)'
          : `Regional request failed (401)`,
    });
  }
  if (response.status === 403) {
    return new RegionalApiError({
      kind: 'forbidden',
      status: 403,
      message: `Regional request failed (403)`,
    });
  }
  return new RegionalApiError({
    kind: 'http',
    status: response.status,
    reminted: opts.reminted,
    message: `Regional request failed (${response.status})`,
  });
};
